/**
 * LocationWizard - Main wizard dialog for creating/editing locations
 *
 * A 3-step wizard that guides users through:
 * 1. Airport & Identity - airport selection, name, code, status
 * 2. Airport Details - IATA, city, state, timezone, contact info
 * 3. Adjust Location - map pin placement with reverse geocoding
 *
 * Supports both create and edit modes.
 * Follows the same pattern as AddPlaneWizard.
 */

import { useCallback, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'

import { WizardStepIndicatorWithLabels } from '@/components/ramp-wizard/WizardStepIndicator'
import { AirportIdentityStep } from './steps/AirportIdentityStep'
import { AirportDetailsStep } from './steps/AirportDetailsStep'
import { AdjustLocationStep } from './steps/AdjustLocationStep'
import {
  useLocationWizardStore,
  LOCATION_WIZARD_STEPS,
  type LocationWizardStep,
} from '@/stores/locationWizardStore'
import {
  useLocation,
  useCreateLocation,
  useUpdateLocation,
} from '@/hooks/useLocations'

export function LocationWizard() {
  const {
    isOpen,
    currentStep,
    mode,
    editLocationId,
    isSubmitting,
    close,
    reset,
    nextStep,
    prevStep,
    goToStep,
    isStepComplete,
    canProceedToStep,
    getFormData,
    setSubmitting,
    populateFromLocation,
  } = useLocationWizardStore()

  // Fetch location data in edit mode
  const { data: editLocation, isLoading: isLoadingLocation } = useLocation(
    editLocationId ?? undefined
  )

  // Mutations
  const createMutation = useCreateLocation()
  const updateMutation = useUpdateLocation()

  const isMutating = createMutation.isPending || updateMutation.isPending

  // Populate form when edit location data arrives
  useEffect(() => {
    if (mode === 'edit' && editLocation && editLocationId) {
      populateFromLocation(editLocation)
    }
  }, [mode, editLocation, editLocationId, populateFromLocation])

  // Handle step navigation via click
  const handleStepClick = useCallback(
    (step: number) => {
      if (canProceedToStep(step as LocationWizardStep)) {
        goToStep(step as LocationWizardStep)
      }
    },
    [canProceedToStep, goToStep]
  )

  // Handle next button
  const handleNext = useCallback(() => {
    const next = Math.min(currentStep + 1, 2) as LocationWizardStep
    if (canProceedToStep(next)) {
      nextStep()
    }
  }, [currentStep, canProceedToStep, nextStep])

  // Handle final submission
  const handleSubmit = useCallback(async () => {
    const formData = getFormData()

    setSubmitting(true)
    try {
      if (mode === 'edit' && editLocationId) {
        await updateMutation.mutateAsync({ id: editLocationId, data: formData })
      } else {
        await createMutation.mutateAsync(formData)
      }
      reset()
    } catch {
      // Error handling is done in the mutation hooks (toast notifications)
    } finally {
      setSubmitting(false)
    }
  }, [
    getFormData,
    mode,
    editLocationId,
    createMutation,
    updateMutation,
    reset,
    setSubmitting,
  ])

  // Handle dialog close
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close()
        reset()
      }
    },
    [close, reset]
  )

  const nextStepIndex = Math.min(currentStep + 1, 2) as LocationWizardStep
  const canProceed = canProceedToStep(nextStepIndex)
  const isLastStep = currentStep === 2

  const isEdit = mode === 'edit'

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[800px] p-0">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-bold">
            {isEdit ? 'Edit Location' : 'Add New Location'}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {LOCATION_WIZARD_STEPS[currentStep]} - Step {currentStep + 1} of{' '}
            {LOCATION_WIZARD_STEPS.length}
          </DialogDescription>
        </DialogHeader>

        {/* Loading state for edit mode */}
        {isEdit && isLoadingLocation ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">
              Loading location details...
            </span>
          </div>
        ) : (
          <>
            {/* Step Indicator */}
            <div className="px-6 pt-4">
              <WizardStepIndicatorWithLabels
                steps={LOCATION_WIZARD_STEPS}
                currentStep={currentStep}
                isStepComplete={isStepComplete}
                onStepClick={handleStepClick}
                canNavigate
              />
            </div>

            {/* Step Content */}
            {/* Using CSS hidden class instead of conditional render to prevent Dialog onOpenChange trigger */}
            <div className="px-6 py-4">
              <div className={currentStep === 0 ? '' : 'hidden'}>
                <AirportIdentityStep />
              </div>
              <div className={currentStep === 1 ? '' : 'hidden'}>
                <AirportDetailsStep />
              </div>
              <div className={currentStep === 2 ? '' : 'hidden'}>
                <AdjustLocationStep />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center px-6 py-4 border-t bg-muted/30">
              <Button
                type="button"
                variant="ghost"
                onClick={prevStep}
                disabled={currentStep === 0 || isSubmitting || isMutating}
              >
                Back
              </Button>

              {isLastStep ? (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isMutating}
                >
                  {(isSubmitting || isMutating) && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {isEdit ? 'Update Location' : 'Create Location'}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={!canProceed || isSubmitting}
                >
                  Next
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default LocationWizard
